import { useEffect, useRef } from 'react';
import useRolloverToastStore from '@/stores/useRolloverToastStore';
import type { RolloverToastData } from '@/stores/useRolloverToastStore';
import { formatSlotWindow12 } from '@/utils/helpers';

// A row of the current Pickups-tab list, narrowed to only what the rollover
// toast derivation reads. Kept structural so callers can pass their mapped
// pickup rows without a bespoke type.
export interface RolloverCandidateRow {
  key: string;
  slot_response_open?: boolean;
  slot?: {
    code?: string;
    label?: string;
    window_from?: string;
    window_to?: string;
  };
}

const buildPayload = (row: RolloverCandidateRow): RolloverToastData => ({
  pickupUid: row.key,
  toSlotCode: row.slot?.code ?? '',
  toSlotLabel: row.slot?.label ?? '',
  toSlotWindow: formatSlotWindow12(row.slot?.window_from, row.slot?.window_to),
});

const samePayload = (a: RolloverToastData | null, b: RolloverToastData | null): boolean =>
  a === b ||
  (!!a &&
    !!b &&
    a.pickupUid === b.pickupUid &&
    a.toSlotCode === b.toSlotCode &&
    a.toSlotLabel === b.toSlotLabel &&
    a.toSlotWindow === b.toSlotWindow);

// Derive the singleton RolloverToast target from the current Pickups list.
// This is a polled SPA (no websocket/push): the page recomputes the toast
// itself — the most-recent row with slot_response_open === true whose uid
// hasn't been session-dismissed — and pushes it into useRolloverToastStore.
//
// React #185 regression (defense-in-depth): the previous inline effect called
// setData(new object) on EVERY run and depended on the pickup rows array. The
// pickup list hook used to hand back a fresh array reference each render (now
// memoized — see usePickupData), so the effect fired every render and, whenever
// a slot_response_open pickup existed, set a brand-new toast object each time →
// render → setState → "Maximum update depth exceeded". The content-equality
// bail below makes the emit idempotent: setData fires ONLY when the derived
// toast payload actually changes, so even an unstable rows reference can never
// respin the loop. Reading the live store value through a ref keeps the guard
// current without adding `data` to the dependency list (which would itself
// re-run the effect on every emit).
export const useRolloverToastSync = (
  rows: RolloverCandidateRow[],
  isActive: boolean,
): void => {
  const dismissedUids = useRolloverToastStore((state) => state.dismissedUids);
  const setData = useRolloverToastStore((state) => state.setData);
  const data = useRolloverToastStore((state) => state.data);

  const dataRef = useRef(data);
  dataRef.current = data;

  useEffect(() => {
    if (!isActive) return;

    const candidate = rows.find(
      (row) => row.slot_response_open && !dismissedUids.includes(row.key),
    );

    if (candidate) {
      const next = buildPayload(candidate);
      if (!samePayload(dataRef.current, next)) {
        setData(next);
      }
    } else if (
      dataRef.current &&
      !rows.some((row) => row.key === dataRef.current?.pickupUid)
    ) {
      // The previously-toasted pickup no longer qualifies (resolved elsewhere,
      // left the current page, etc.) — clear the stale toast.
      setData(null);
    }
  }, [rows, dismissedUids, isActive, setData]);
};
